/**
 * Access Control Types
 * Enterprise-grade type definitions for RBAC + PBAC system
 * 
 * @author JGS Tech Team
 * @version 1.0
 */

// ==================== Permission Types ====================

/**
 * Permission scope defines the extent of access
 */
export type PermissionScope = 'own' | 'team' | 'all';

/**
 * Permission override type
 */
export type PermissionOverrideType = 'GRANT' | 'REVOKE';

/**
 * Available user roles in the system
 */
export type UserRole = 
    | 'ROLE_VIEWER'
    | 'ROLE_AUTHOR'
    | 'ROLE_EDITOR'
    | 'ROLE_PUBLISHER'
    | 'ROLE_ADMIN';

/**
 * Permission entity from backend
 */
export interface Permission {
    permissionId: string;
    permissionCode: string;
    displayName: string;
    description: string;
    resource: string;
    action: string;
    scope: PermissionScope;
    category: string;
    isSystem: boolean;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

/**
 * User permission override entity
 */
export interface UserPermissionOverride {
    overrideId: string;
    userId: string;
    permissionCode: string;
    overrideType: PermissionOverrideType;
    reason: string;
    grantedBy: string;
    grantedAt: string;
    expiresAt: string | null;
    isActive: boolean;
}

/**
 * Permission audit log entry
 */
export interface PermissionAuditLog {
    auditId: string;
    action: string;
    targetUserId: string;
    targetUsername: string;
    permissionCode: string;
    previousValue: string;
    newValue: string;
    performedBy: string;
    performedByUsername: string;
    ipAddress: string;
    userAgent: string;
    createdAt: string;
}

// ==================== Request/Response Types ====================

/**
 * Permission payload for create/update operations
 */
export interface PermissionPayload {
    permissionCode: string;
    displayName: string;
    description?: string;
    resource: string;
    action: string;
    scope?: PermissionScope;
    category: string;
    isSystem?: boolean;
}

/**
 * Override payload for grant/revoke operations
 */
export interface PermissionOverridePayload {
    permissionCode: string;
    reason?: string;
    expiresAt?: string;
}

/**
 * Role permission update payload
 */
export interface RolePermissionPayload {
    permissionCodes: string[];
}

/**
 * Complete user permissions response from backend
 */
export interface UserPermissionsResponse {
    userId: string;
    username: string;
    roles: string[];
    rolePermissions: string[];
    grantedOverrides: string[];
    revokedOverrides: string[];
    effectivePermissions: string[];
    permissionDetails: Record<string, Permission>;
}

// ==================== Context Types ====================

/**
 * Access Control context state
 */
export interface AccessControlState {
    /** Current user's roles */
    roles: string[];
    /** Current user's effective permissions */
    permissions: Set<string>;
    /** Full permission details (for UI) */
    permissionDetails: Record<string, Permission>;
    /** Loading state */
    isLoading: boolean;
    /** Error state */
    error: string | null;
    /** Last refresh timestamp */
    lastRefreshed: number | null;
}

/**
 * Access Control context value (state + actions)
 */
export interface AccessControlContextValue extends AccessControlState {
    /** Whether the initial auth check is still in progress (app should block rendering until false) */
    isInitializing: boolean;
    /** Check if user has a specific permission */
    hasPermission: (permissionCode: string) => boolean;
    /** Check if user has all specified permissions */
    hasAllPermissions: (permissionCodes: string[]) => boolean;
    /** Check if user has any of the specified permissions */
    hasAnyPermission: (permissionCodes: string[]) => boolean;
    /** Check if user has a specific role */
    hasRole: (role: UserRole | string) => boolean;
    /** Check if user has any of the specified roles */
    hasAnyRole: (roles: (UserRole | string)[]) => boolean;
    /** Check if user is admin */
    isAdmin: () => boolean;
    /** Check if user is authenticated (has at least one role) */
    isAuthenticated: boolean;
    /** Refresh permissions from server */
    refreshPermissions: () => Promise<void>;
    /** Clear all access control state */
    clearAccessControl: () => void;
    /** Set permissions from login response */
    setPermissionsFromLogin: (roles: string[], permissions: string[]) => void;
}

// ==================== Component Props Types ====================

/**
 * Props for PermissionGate component
 */
export interface PermissionGateProps {
    /** Required permission code */
    permission?: string;
    /** Required permissions (all must be present) */
    permissions?: string[];
    /** Required permissions (any one is sufficient) */
    anyPermissions?: string[];
    /** Required role */
    role?: UserRole | string;
    /** Required roles (any one is sufficient) */
    anyRoles?: (UserRole | string)[];
    /** Content to render if authorized */
    children: React.ReactNode;
    /** Fallback content if unauthorized */
    fallback?: React.ReactNode;
    /** Whether to show loading state */
    showLoading?: boolean;
}

/**
 * Props for RoleGate component
 */
export interface RoleGateProps {
    /** Required role */
    role?: UserRole | string;
    /** Required roles (all must be present) */
    roles?: (UserRole | string)[];
    /** Required roles (any one is sufficient) */
    anyRoles?: (UserRole | string)[];
    /** Content to render if authorized */
    children: React.ReactNode;
    /** Fallback content if unauthorized */
    fallback?: React.ReactNode;
}

/**
 * Props for AdminOnly component
 */
export interface AdminOnlyProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

// ==================== Menu Types ====================

/**
 * Permission requirement for menu items
 */
export interface MenuPermissionConfig {
    /** Required permission code */
    permission?: string;
    /** Required permissions (any one is sufficient) */
    anyPermissions?: string[];
    /** Required permissions (all must be present) */
    allPermissions?: string[];
    /** Required role */
    role?: UserRole | string;
    /** Required roles (any one is sufficient) */
    anyRoles?: (UserRole | string)[];
    /** Hide menu item when user is authenticated (e.g., login, signup) */
    hideWhenAuthenticated?: boolean;
    /** Always hide from menu (e.g., 404 page) */
    hideFromMenu?: boolean;
}

/**
 * Extended menu item with permission config
 */
export interface MenuItemWithPermission {
    id: string;
    text: string;
    path: string;
    icon?: string;
    isActive?: boolean;
    subMenu?: MenuItemWithPermission[];
    permissions?: MenuPermissionConfig;
}

// ==================== Route Types ====================

/**
 * Protected route configuration
 */
export interface ProtectedRouteConfig {
    path: string;
    element: React.ReactNode;
    permissions?: MenuPermissionConfig;
    redirectTo?: string;
}

// ==================== Admin Types ====================

/**
 * Role permission matrix (role -> permissions)
 */
export type RolePermissionMatrix = Record<string, string[]>;

/**
 * Permission grouped by category
 */
export type GroupedPermissions = Record<string, Permission[]>;

/**
 * User for access control management
 */
export interface AccessControlUser {
    userId: string;
    username: string;
    email: string;
    name: string;
    surname: string;
    roles: string[];
    hasOverrides: boolean;
}

/**
 * Permission validation result
 */
export interface PermissionValidationResult {
    permissionCode: string;
    hasPermission: boolean;
}

/**
 * Bulk permission validation result
 */
export interface BulkPermissionValidationResult {
    permissionCodes: string[];
    mode: 'any' | 'all';
    result: boolean;
}

// ==================== Constants ====================

/**
 * Standard permission codes used across the application
 */
export const PERMISSION_CODES = {
    // Dashboard
    DASHBOARD_VIEW: 'dashboard:view:all',
    
    // Articles
    ARTICLES_VIEW_OWN: 'articles:view:own',
    ARTICLES_VIEW_ALL: 'articles:view:all',
    ARTICLES_CREATE: 'articles:create:own',
    ARTICLES_EDIT_OWN: 'articles:edit:own',
    ARTICLES_EDIT_ALL: 'articles:edit:all',
    ARTICLES_DELETE_OWN: 'articles:delete:own',
    ARTICLES_DELETE_ALL: 'articles:delete:all',
    ARTICLES_SUBMIT: 'articles:submit:own',
    ARTICLES_APPROVE: 'articles:approve:all',
    ARTICLES_PUBLISH: 'articles:publish:all',
    ARTICLES_SCHEDULE: 'articles:schedule:all',
    
    // Categories
    CATEGORIES_VIEW: 'categories:view:all',
    CATEGORIES_CREATE: 'categories:create:all',
    CATEGORIES_EDIT: 'categories:edit:all',
    CATEGORIES_DELETE: 'categories:delete:all',
    
    // Users
    USERS_VIEW: 'users:view:all',
    USERS_CREATE: 'users:create:all',
    USERS_EDIT: 'users:edit:all',
    USERS_DELETE: 'users:delete:all',
    
    // Workflow
    WORKFLOW_VIEW: 'workflow:view:all',
    WORKFLOW_MANAGE: 'workflow:manage:all',
    
    // Notifications
    NOTIFICATIONS_VIEW: 'notifications:view:all',
    NOTIFICATIONS_SEND: 'notifications:send:all',
    
    // Analytics
    ANALYTICS_VIEW: 'analytics:view:all',
    
    // Settings
    SETTINGS_VIEW: 'settings:view:all',
    SETTINGS_MANAGE: 'settings:manage:all',
    
    // Access Control
    ACCESS_CONTROL_VIEW: 'access-control:view:all',
    ACCESS_CONTROL_MANAGE: 'access-control:manage:all',
    
    // AI Features
    AI_FEATURES_USE: 'ai:use:all',
    AI_FEATURES_MANAGE: 'ai:manage:all',

    // Web Series
    WEB_SERIES_VIEW: 'web-series:view:all',
    WEB_SERIES_CREATE: 'web-series:create:all',
    WEB_SERIES_EDIT: 'web-series:edit:all',
    WEB_SERIES_DELETE: 'web-series:delete:all',
    WEB_SERIES_PUBLISH: 'web-series:publish:all',
    WEB_SERIES_SECTIONS_MANAGE: 'web-series:sections:manage',
} as const;

/**
 * Role display names
 */
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
    ROLE_VIEWER: 'Viewer',
    ROLE_AUTHOR: 'Author',
    ROLE_EDITOR: 'Editor',
    ROLE_PUBLISHER: 'Publisher',
    ROLE_ADMIN: 'Administrator',
};

/**
 * Permission categories
 */
export const PERMISSION_CATEGORIES = [
    'Dashboard',
    'Articles',
    'Categories',
    'Users',
    'Workflow',
    'Notifications',
    'Analytics',
    'Settings',
    'Access Control',
    'AI Features',
] as const;

export type PermissionCategory = typeof PERMISSION_CATEGORIES[number];
